/**
 * PlannerPage - Main page for lesson planning
 *
 * Features:
 * - Week planner with Mon-Fri view
 * - Class selector
 * - Loads curriculum outcomes from parsed_full.json
 * - Fetches/saves plans via plannerService
 * - Auto-save blocks on change
 * - Loading states and error handling
 * - AbortController for cleanup
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { WeekPlanner } from "@/components/planner/WeekPlanner";
import {
  type CurriculumOutcome,
  type WeekPlan,
  type TimeBlock as UITimeBlock,
  type DayOfWeek,
  DAYS_OF_WEEK,
  getWeekStart,
} from "@/components/planner/types";
import { plannerService } from "@/services/planner.service";
import { classService, type ServerClass } from "@/services/class.service";
import { useLocalDbStore } from "@/store/localDbStore";
import { toast } from "sonner";
import {
  Calendar,
  Users,
  AlertCircle,
  Loader2,
  BookOpen,
  Plus,
  RefreshCw,
} from "lucide-react";

// Import curriculum data
import curriculumData from "../../../curriculum/parsed_full.json";

// ============================================================================
// Types
// ============================================================================

interface PlannerState {
  weekStart: Date;
  selectedClassId: string | null;
  plan: WeekPlan | null;
  planId: string | null;
}

// ============================================================================
// Helper Functions
// ============================================================================

function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error && typeof error === "object") {
    const err = error as { code?: string; name?: string };
    if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
      return true;
    }
  }
  return false;
}

/**
 * Convert day of week number (0-4) to DayOfWeek string
 */
function dayNumberToString(dayNum: number): DayOfWeek {
  return DAYS_OF_WEEK[dayNum] || "Monday";
}

/**
 * Convert DayOfWeek string to day number (0-4)
 */
function dayStringToNumber(day: DayOfWeek): number {
  return DAYS_OF_WEEK.indexOf(day);
}

/**
 * Transform API Block to UI TimeBlock
 */
function transformBlockToUI(block: {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  subject: string;
  outcomeIds: string[];
  notes?: string;
}): UITimeBlock {
  return {
    id: block.id,
    day: dayNumberToString(block.dayOfWeek),
    startTime: block.startTime,
    endTime: block.endTime,
    subject: block.subject as UITimeBlock["subject"],
    title: block.subject || "",
    notes: block.notes || "",
    outcomeIds: block.outcomeIds || [],
    resources: [],
  };
}

// ============================================================================
// Constants
// ============================================================================

const SELECTED_CLASS_KEY = "quickgrade_planner_selected_class";

// ============================================================================
// Main Component
// ============================================================================

export default function PlannerPage() {
  // Curriculum outcomes (loaded from JSON)
  const outcomes = useMemo<CurriculumOutcome[]>(
    () => curriculumData as CurriculumOutcome[],
    []
  );

  // State
  const [state, setState] = useState<PlannerState>(() => ({
    weekStart: getWeekStart(new Date()),
    selectedClassId: localStorage.getItem(SELECTED_CLASS_KEY) || null,
    plan: null,
    planId: null,
  }));
  const [serverClasses, setServerClasses] = useState<ServerClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [classesLoading, setClassesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local DB state for class display names
  const localClasses = useLocalDbStore((s) => s.classes);
  const isLocalDbConnected = useLocalDbStore((s) => s.isConnected);

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Fetch server classes on mount
  useEffect(() => {
    const controller = new AbortController();

    const fetchClasses = async () => {
      try {
        setClassesLoading(true);
        const classes = await classService.getClasses(false, controller.signal);
        if (!controller.signal.aborted) {
          setServerClasses(classes);

          // If no class selected but we have classes, select first
          if (!state.selectedClassId && classes.length > 0) {
            const firstClassId = classes[0].classId;
            setState((prev) => ({ ...prev, selectedClassId: firstClassId }));
            localStorage.setItem(SELECTED_CLASS_KEY, firstClassId);
          }
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to load classes:", err);
          // Don't show error toast for this - classes are optional
        }
      } finally {
        if (!controller.signal.aborted) {
          setClassesLoading(false);
        }
      }
    };

    fetchClasses();

    return () => controller.abort();
  }, []);

  // Fetch or create plan when week or class changes
  useEffect(() => {
    const { weekStart, selectedClassId } = state;

    if (!selectedClassId) {
      setLoading(false);
      setState((prev) => ({ ...prev, plan: null, planId: null }));
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const fetchPlan = async () => {
      try {
        setLoading(true);
        setError(null);

        const weekStartStr = plannerService.getWeekStartForDate(weekStart);

        // Try to find existing plan for this class and week
        const plans = await plannerService.getPlans(
          selectedClassId,
          weekStartStr,
          controller.signal
        );

        if (controller.signal.aborted) return;

        if (plans.length > 0) {
          // Load existing plan with blocks
          const planWithBlocks = await plannerService.getPlan(
            plans[0].id,
            controller.signal
          );

          if (controller.signal.aborted) return;

          // Transform to UI format
          const uiPlan: WeekPlan = {
            id: planWithBlocks.id,
            weekStart: planWithBlocks.weekStart,
            classId: planWithBlocks.classId,
            blocks: planWithBlocks.blocks.map(transformBlockToUI),
          };

          setState((prev) => ({
            ...prev,
            plan: uiPlan,
            planId: planWithBlocks.id,
          }));
        } else {
          // No plan exists - create one
          const newPlan = await plannerService.createPlan(
            { classId: selectedClassId, weekStart: weekStartStr },
            controller.signal
          );

          if (controller.signal.aborted) return;

          const uiPlan: WeekPlan = {
            id: newPlan.id,
            weekStart: newPlan.weekStart,
            classId: newPlan.classId,
            blocks: [],
          };

          setState((prev) => ({
            ...prev,
            plan: uiPlan,
            planId: newPlan.id,
          }));
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to load plan:", err);
          const message = err instanceof Error ? err.message : "Failed to load plan";
          setError(message);
          toast.error("Failed to load plan");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchPlan();

    return () => controller.abort();
  }, [state.weekStart, state.selectedClassId]);

  // Handle week change
  const handleWeekChange = useCallback((newWeekStart: Date) => {
    setState((prev) => ({ ...prev, weekStart: newWeekStart }));
  }, []);

  // Handle class selection
  const handleClassChange = useCallback((classId: string) => {
    setState((prev) => ({ ...prev, selectedClassId: classId }));
    localStorage.setItem(SELECTED_CLASS_KEY, classId);
  }, []);

  // Handle block save (create or update)
  const handleBlockSave = useCallback(
    async (blockData: Omit<UITimeBlock, "id"> & { id?: string }) => {
      const { planId, plan } = state;

      if (!planId) {
        toast.error("No plan selected");
        return;
      }

      // Cancel any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Optimistic update
      const tempId = blockData.id || `temp-${Date.now()}`;
      const optimisticBlock: UITimeBlock = {
        ...blockData,
        id: tempId,
        title: blockData.title || blockData.subject || "",
        notes: blockData.notes || "",
        outcomeIds: blockData.outcomeIds || [],
        resources: blockData.resources || [],
      };

      setState((prev) => {
        if (!prev.plan) return prev;

        const existingIndex = prev.plan.blocks.findIndex(
          (b) => b.id === blockData.id
        );

        const newBlocks =
          existingIndex >= 0
            ? prev.plan.blocks.map((b, i) =>
                i === existingIndex ? optimisticBlock : b
              )
            : [...prev.plan.blocks, optimisticBlock];

        return {
          ...prev,
          plan: { ...prev.plan, blocks: newBlocks },
        };
      });

      // Debounced save to server
      saveTimeoutRef.current = setTimeout(async () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
          setSaving(true);

          const apiBlockData = {
            dayOfWeek: dayStringToNumber(blockData.day),
            startTime: blockData.startTime,
            endTime: blockData.endTime,
            subject: blockData.subject || "",
            outcomeIds: blockData.outcomeIds || [],
            notes: blockData.notes,
          };

          let savedBlock;

          if (blockData.id && !blockData.id.startsWith("temp-")) {
            // Update existing block
            savedBlock = await plannerService.updateBlock(
              planId,
              blockData.id,
              apiBlockData,
              controller.signal
            );
          } else {
            // Create new block
            savedBlock = await plannerService.createBlock(
              planId,
              apiBlockData,
              controller.signal
            );
          }

          if (controller.signal.aborted) return;

          // Update with real ID from server
          const uiBlock = transformBlockToUI(savedBlock);

          setState((prev) => {
            if (!prev.plan) return prev;

            const newBlocks = prev.plan.blocks.map((b) =>
              b.id === tempId || b.id === blockData.id ? uiBlock : b
            );

            return {
              ...prev,
              plan: { ...prev.plan, blocks: newBlocks },
            };
          });

          toast.success("Block saved");
        } catch (err) {
          if (!isRequestCancelled(err)) {
            console.error("Failed to save block:", err);
            toast.error("Failed to save block");

            // Revert optimistic update on error
            setState((prev) => {
              if (!prev.plan) return prev;

              const newBlocks = prev.plan.blocks.filter(
                (b) => b.id !== tempId
              );

              return {
                ...prev,
                plan: { ...prev.plan, blocks: newBlocks },
              };
            });
          }
        } finally {
          if (!controller.signal.aborted) {
            setSaving(false);
          }
        }
      }, 500); // 500ms debounce
    },
    [state.planId, state.plan]
  );

  // Handle block delete
  const handleBlockDelete = useCallback(
    async (blockId: string) => {
      const { planId } = state;

      if (!planId) {
        toast.error("No plan selected");
        return;
      }

      // Skip if it's a temp block (not saved to server yet)
      if (blockId.startsWith("temp-")) {
        setState((prev) => {
          if (!prev.plan) return prev;

          return {
            ...prev,
            plan: {
              ...prev.plan,
              blocks: prev.plan.blocks.filter((b) => b.id !== blockId),
            },
          };
        });
        return;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Optimistic delete
      const deletedBlock = state.plan?.blocks.find((b) => b.id === blockId);

      setState((prev) => {
        if (!prev.plan) return prev;

        return {
          ...prev,
          plan: {
            ...prev.plan,
            blocks: prev.plan.blocks.filter((b) => b.id !== blockId),
          },
        };
      });

      try {
        await plannerService.deleteBlock(planId, blockId, controller.signal);

        if (controller.signal.aborted) return;

        toast.success("Block deleted");
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to delete block:", err);
          toast.error("Failed to delete block");

          // Revert optimistic delete
          if (deletedBlock) {
            setState((prev) => {
              if (!prev.plan) return prev;

              return {
                ...prev,
                plan: {
                  ...prev.plan,
                  blocks: [...prev.plan.blocks, deletedBlock],
                },
              };
            });
          }
        }
      }
    },
    [state.planId, state.plan]
  );

  // Get class display name (prefer local name, fall back to server name)
  const getClassDisplayName = useCallback(
    (serverClassId: string): string => {
      // Try to find matching local class by serverClassId
      const localClass = localClasses.find(
        (c) => c.serverClassId === serverClassId
      );
      if (localClass) {
        return localClass.name + (localClass.grade ? ` (${localClass.grade})` : "");
      }

      // Fall back to server class name
      const serverClass = serverClasses.find((c) => c.classId === serverClassId);
      if (serverClass) {
        return serverClass.name + (serverClass.grade ? ` (${serverClass.grade})` : "");
      }

      return "Unknown Class";
    },
    [localClasses, serverClasses]
  );

  // Refresh plan
  const handleRefresh = useCallback(() => {
    // Re-trigger the effect by changing weekStart to itself
    setState((prev) => ({ ...prev, weekStart: new Date(prev.weekStart) }));
  }, []);

  // Render no classes state
  if (!classesLoading && serverClasses.length === 0) {
    return (
      <div className="container mx-auto p-4 sm:p-6 max-w-5xl">
        <div className="space-y-2 mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-7 w-7" />
            Lesson Planner
          </h1>
          <p className="text-muted-foreground">
            Plan your weekly lessons and link curriculum outcomes
          </p>
        </div>

        <Card className="p-8 text-center">
          <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <CardTitle className="mb-2">No Classes Found</CardTitle>
          <CardDescription className="mb-4">
            Create a class first to start planning lessons.
          </CardDescription>
          <Button asChild>
            <a href="/students">
              <Plus className="h-4 w-4 mr-2" />
              Create a Class
            </a>
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="space-y-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-7 w-7" />
            Lesson Planner
          </h1>
          <p className="text-muted-foreground">
            Plan your weekly lessons and link curriculum outcomes
          </p>
        </div>

        {/* Class Selector */}
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh plan"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          {classesLoading ? (
            <Skeleton className="h-10 w-[180px]" />
          ) : (
            <Select
              value={state.selectedClassId || ""}
              onValueChange={handleClassChange}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select a class..." />
              </SelectTrigger>
              <SelectContent>
                {serverClasses.map((cls) => (
                  <SelectItem key={cls.classId} value={cls.classId}>
                    <span className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {getClassDisplayName(cls.classId)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Curriculum outcomes count */}
      <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
        <BookOpen className="h-4 w-4" />
        <span>{outcomes.length} curriculum outcomes available</span>
      </div>

      {/* Week Planner */}
      <WeekPlanner
        initialWeek={state.weekStart}
        weekPlan={state.plan}
        outcomes={outcomes}
        isLoading={loading}
        onWeekChange={handleWeekChange}
        onBlockSave={handleBlockSave}
        onBlockDelete={handleBlockDelete}
      />
    </div>
  );
}
